import { useTranslation } from "react-i18next";
import {
  SearchedSubjectsItem,
  SearchedSubjectsList,
} from "../../../redux/apiSpecifications/apiSubjects";
import { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_PaginationState,
} from "material-react-table";
import { CellWrapper } from "../../../style/CellWrapper";
import { TableWrapper } from "../../../style/TableWrapper";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { MRT_Localization_IT } from "material-react-table/locales/it";
import { MRT_Localization_EN } from "material-react-table/locales/en";

interface ISubjectsListProps {
  subjects: SearchedSubjectsList;
  onSelectItem?: (item: SubjectsListItem) => void;
  onPaginationChange: (pageIndex: number, pageSize: number) => void;
}

const SubjectsList = ({
  subjects,
  onSelectItem,
  onPaginationChange,
}: ISubjectsListProps) => {
  const { t } = useTranslation();

  const { language } = useSelector((state: RootState) => state.user) || {};

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: subjects.currentPageNum! - 1,
    pageSize: subjects.pageLen!,
  });

  useEffect(() => {
    if (
      subjects.currentPageNum! - 1 !== pagination.pageIndex ||
      subjects.pageLen! !== pagination.pageSize
    ) {
      onPaginationChange(pagination.pageIndex + 1, pagination.pageSize);
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  const items = useMemo(
    () =>
      subjects.items?.map((item) => {
        return {
          ...item,
          taxId: item.pIva ? item.pIva : item.fiscalCode,
        } as SubjectsListItem;
      }) || [],
    [subjects.items]
  );

  const columns = useMemo<MRT_ColumnDef<SubjectsListItem>[]>(() => {
    return [
      {
        accessorKey: "name",
        header: t("LABELS.subjects-list-name-column-header"),
        size: 100,
        Cell: ({ row }) => <CellWrapper>{row.original.name}</CellWrapper>,
      },
      {
        accessorKey: "taxId",
        header: t("LABELS.subjects-list-effect-taxId-column-header"),
        size: 100,
        Cell: ({ row }) => <CellWrapper>{row.original.taxId}</CellWrapper>,
      },
      {
        accessorKey: "preferredEmail",
        header: t("LABELS.subjects-list-preferredEmail-column-header"),
        size: 100,
        Cell: ({ row }) => (
          <CellWrapper>{row.original.preferredEmail}</CellWrapper>
        ),
      },
      {
        accessorKey: "legalEmail",
        header: t("LABELS.subjects-list-legalEmail-column-header"),
        size: 100,
        Cell: ({ row }) => <CellWrapper>{row.original.legalEmail}</CellWrapper>,
      },
      {
        accessorKey: "preferredPhone",
        header: t("LABELS.subjects-list-preferredPhone-column-header"),
        size: 100,
        Cell: ({ row }) => (
          <CellWrapper>{row.original.preferredPhone}</CellWrapper>
        ),
      },
      // {
      //   accessorKey: "expirationDateNumber",
      //   header: t("policies-list-expiration-date-column-header"),
      //   size: 100,
      //   Cell: ({ row }) => (
      //     <CellWrapper>{row.original.expirationDate}</CellWrapper>
      //   ),
      // },
    ];
  }, [subjects.items, language]);

  const handleSelectItem = (item: SubjectsListItem) => {
    if (onSelectItem) onSelectItem(item);
  };

  return (
    <TableWrapper>
      <MaterialReactTable
        columns={columns}
        data={items}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enableFilters={false}
        enableColumnActions={false}
        enableColumnDragging={false}
        enableSorting={false}
        enableTopToolbar={false}
        localization={
          language === "it" ? MRT_Localization_IT : MRT_Localization_EN
        }
        muiTableBodyRowProps={({ row }) => ({
          onClick: (event) => {
            handleSelectItem(row.original);
          },
          sx: {
            cursor: onSelectItem !== undefined ? "pointer" : "default",
          },
        })}
        state={{
          pagination,
        }}
        manualPagination
        rowCount={subjects.totalCount!}
        onPaginationChange={setPagination}
      />
      {/* <pre>{JSON.stringify(items, null, 2)}</pre> */}
    </TableWrapper>
  );
};

export default SubjectsList;

export type SubjectsListItem = SearchedSubjectsItem & { taxId: string };
